/**
 * 룰 상세 "쌓인 메모" 목록 organism (D-41).
 *
 * @spec:2026-09-29-처방검토-판정기록-조회화면
 *
 * notes 응답 중 kind==="review"만 시간 역순(createdAt 내림, 동률 id 내림)으로 보인다.
 * 항목 = 작성자(authorName ?? author)·+09:00 시각·본문·vN(ruleRowId→버전 목록, 없으면 notes.version,
 * 그것도 없으면 "버전 미상"). 본문이 판정 기록 접두면 "판정 기록 #<id> 보기" 새 탭 링크(경로는 prop).
 * 이 부품은 판정 기록 라우트·사용자 조회 API를 모른다(ARCHITECTURE §2, D-41). 절 제목은 "쌓인 메모"이고
 * 룰 검수 화면의 기존 라벨과 겹치는 문구를 쓰지 않는다(S7 — 기존 e2e 부분 일치 단정과 충돌 방지).
 */
import React from "react";
import Button from "components/atoms/Button";
import { parseDoseReviewLogNoteRef, formatKstMinute } from "utils/doseRule";
import type {
  AdminDoseRuleNote,
  AdminDoseRuleVersionListItem,
} from "services/api/doseRules/types";
import {
  Section,
  Title,
  List,
  Item,
  Meta,
  Author,
  VersionTag,
  Body,
  LogLink,
  StateText,
  ErrorRow,
} from "./style";

export const NOTE_LIST_TITLE = "쌓인 메모";
export const NOTE_LIST_LOADING = "…";
export const NOTE_LIST_EMPTY = "쌓인 메모가 없습니다";
export const NOTE_LIST_ERROR = "메모를 불러오지 못했습니다";
export const NOTE_LIST_RETRY = "다시 시도";
const VERSION_UNKNOWN = "버전 미상";

export interface DoseRuleNoteListProps {
  notes: AdminDoseRuleNote[] | undefined;
  versions: AdminDoseRuleVersionListItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** 본문이 판정 기록 접두일 때 링크 경로(organism은 라우트를 모른다). null이면 링크 없음. */
  noteHref?: (note: AdminDoseRuleNote) => string | null;
}

/** 시간 역순(createdAt 내림, null은 맨 뒤) → 동률·null은 id 내림(§3.10 ⑦). */
const byTimeDesc = (a: AdminDoseRuleNote, b: AdminDoseRuleNote): number => {
  const ca = a.createdAt;
  const cb = b.createdAt;
  if (ca && cb) {
    if (ca !== cb) return ca < cb ? 1 : -1;
  } else if (ca && !cb) {
    return -1;
  } else if (!ca && cb) {
    return 1;
  }
  return (b.id ?? 0) - (a.id ?? 0);
};

const DoseRuleNoteList: React.FC<DoseRuleNoteListProps> = ({
  notes,
  versions,
  isLoading,
  isError,
  onRetry,
  noteHref,
}) => {
  const versionLabel = (note: AdminDoseRuleNote): string => {
    const matched = versions?.find((v) => v.id === note.ruleRowId);
    if (typeof matched?.version === "number") return `v${matched.version}`;
    if (typeof note.version === "number") return `v${note.version}`;
    return VERSION_UNKNOWN;
  };

  const reviewNotes = (notes ?? []).filter((n) => n.kind === "review").slice().sort(byTimeDesc);

  return (
    <Section data-testid="dose-rule-note-list">
      <Title>{NOTE_LIST_TITLE}</Title>
      {isLoading ? (
        <StateText>{NOTE_LIST_LOADING}</StateText>
      ) : isError ? (
        <ErrorRow>
          <StateText>{NOTE_LIST_ERROR}</StateText>
          <Button
            buttonType="button"
            type="Text"
            size="Small"
            variant="Outlined"
            color="Secondary"
            onClick={onRetry}
            data-testid="dose-rule-note-retry"
          >
            {NOTE_LIST_RETRY}
          </Button>
        </ErrorRow>
      ) : reviewNotes.length === 0 ? (
        <StateText>{NOTE_LIST_EMPTY}</StateText>
      ) : (
        <List>
          {reviewNotes.map((note) => {
            const ref = parseDoseReviewLogNoteRef(note.body);
            const href = ref && noteHref ? noteHref(note) : null;
            return (
              <Item key={note.id ?? `${note.ruleRowId}-${note.createdAt ?? ""}`} data-testid="dose-rule-note-item">
                <Meta>
                  <Author>{note.authorName ?? note.author ?? "—"}</Author>
                  <span>{formatKstMinute(note.createdAt)}</span>
                  <VersionTag>{versionLabel(note)}</VersionTag>
                </Meta>
                <Body>{note.body}</Body>
                {ref && href ? (
                  <LogLink
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="dose-rule-note-log-link"
                  >
                    판정 기록 #{ref.logId} 보기
                  </LogLink>
                ) : null}
              </Item>
            );
          })}
        </List>
      )}
    </Section>
  );
};

export default DoseRuleNoteList;
